<template>
  <section>
    <div class="content-header">
      <h2>Setting learning community expectations</h2>
    </div>
    <div class="content-wrapper">
      <p>It’s up to us as faculty, instructors and educators to create respectful learning communities where deep learning and critical dialogue can occur, while creating a supportive and compassionate environment in which students can grow. </p>
      <p>We also need to balance voices, and create equitable opportunities for all perspectives and voices to be heard. That means taking an active role in preventing voices from students with dominant identities from dominating participation and contribution in the classroom.</p>
      <p>Building clear learning community expectations with your students is a great strategy to set your classroom up for success, even when navigating difficult conversations. </p>
      <p>To set expectations together, you might say something like:</p>
      <div class="panel mb-3">
        <p>“It’s important to me that we create a respectful learning community, where all voices and perspectives are valued, heard and examined in context of the topics we are discussing. What commitments do we need to make to each other in terms of the ways we engage in this classroom to build a respectful and rigorous learning community?”</p>
      </div>
      <p>You may want to share some examples of things you’d like to see included to get things started, then ask students if there’s anything else they could contribute. Here are some examples:</p>
      <div class="carousel">
        <hooper
          ref="carousel_01"
          class="mb-4"
          :wheelControl="false"
        >
          <slide>
            <div class="slide">
              <p>We will enter the classroom with curiosity and a desire to learn</p>
            </div>
          </slide>

          <slide>
            <div class="slide">
              <p>We will respect the perspectives and lived experiences of others</p>
            </div>
          </slide>

          <slide>
            <div class="slide">
              <p>We will own our own experiences—share the positionality that has influenced our worldview when appropriate and when we feel comfortable</p>
            </div>
          </slide>

          <slide>
            <div class="slide">
              <p>We will challenge stereotypes and generalizations within ourselves and from others</p>
            </div>
          </slide>

          <slide>
            <div class="slide">
              <p>We will be mindful of the space we take up, and ensure many perspectives are given the chance to be heard</p>
            </div>
          </slide>

          <hooper-navigation slot="hooper-addons">
            <img
              slot="hooper-prev"
              src="@/assets/img/_ui/svg/carousel-back.svg"
              alt="previous"
              srcset=""
            >
            <img
              slot="hooper-next"
              src="@/assets/img/_ui/svg/carousel-next.svg"
              alt="next"
              srcset=""
            >
          </hooper-navigation>

          <hooper-pagination slot="hooper-addons"></hooper-pagination>
        </hooper>
      </div>
      <p>Watch Rick Ezekiel set expectations for creating a rigorous learning space. </p>
      <div class="video-player pt-4">
        <div class="ratio ratio-16x9">
  <iframe
    src="https://www.youtube.com/embed/I_fSYt1JliA?rel=0&cc_load_policy=1"
    title="YouTube video"
    allowfullscreen
  ></iframe>
</div>
        <!-- video element -->
        <!-- <vue-plyr>
          <video
            controls
            crossorigin
            playsinline
          >
            <source
              size="720"
              src="https://staging.gevc.ca/centennial/tic/videos/community/design/02.mp4"
              type="video/mp4"
            />
            <track
              label="English"
              kind="subtitles"
              srclang="en"
              src="https://staging.gevc.ca/centennial/tic/cc/community/design/02.en.vtt"
              default
            >
          </video>
        </vue-plyr> -->
        <div class="accordion">
          <accordion
            headerText="Transcript"
            accordionID="transcript01"
          >
            <p>So first off, I want to talk a little bit about some commitments that are important in creating a rigorous learning space, and I want to acknowledge first and foremost that, you know, in this session we are talking about really sensitive topics. We're talking about identity topics, and many identities, we're all going to bring our own identities into this discussion. Right? Some of these topics will have a direct impact on our lived experience, and some of them won't, some of them might be about identities that we've not had lived experience with. So, it's really important that we think about how we enter this space, how we do so with sensitivity and care, and also with rigor, with, you know, a desire to learn and grow. So, just some sort of guidelines, ground rules that I hope to establish at the start of the session. That we're entering with curiosity and a desire to learn. That we commit to respecting the perspectives and the lived experiences of others. This is particularly important too if we're talking about our own lived experiences, or hearing from others, that we really want to own our own lived experiences, right? Share the positionality that's influenced our worldview when it's appropriate, when you're comfortable in doing so. To avoid speaking on behalf of others or for others. Each person gets to be the expert of their own lived experience in this. And we want to problematize that notion that we tell other people's stories, outside of really sharing you know, what we might be able to learn from larger research that enables us to share some of those kind of higher level ideas and concepts. That we’ll challenge stereotyping and generalization in ourselves and in the virtual learning space, we know that we are humans. We are all riddled with bias, and that we all kind of, you know, naturally move towards using stereotyping and generalization to make it easier to understand the world around us. But it's really important that we don't bring those efforts into discussions about identity. Those are the fundamental cognitive patterns that lead to oppression and prejudice in the first place. So, we're going to commit to, you know, challenging those when we're sharing and also, commit to creating a space where we have that sort of social accountability to challenge stereotypes and generalizations as they emerge.</p>
            <p>Then we'll be mindful of the space we take up. When we go into participation components that of course, inviting everyone to share, don't be hesitant to share. But make sure that we have enough space for others to do so as well. And I'll try to be creative with the use of breakout rooms and different ways to engage given that we have a pretty large group here.</p>
            <p>And last but not least, as I've already alluded to, these topics are deeply personal. They're connected to our own identities. We're all gonna have different types of lived experience that touch on one or more of the concepts that we talk about today. So please feel free to take a break, take some space if needed, step away, turn your volume off, you know, engage in some grounding, connect with resources, a trusted colleague, friend or professional resource. And I'll also note that if there is sort of a potential harmful or impactful dialogue emerging, I might encourage us to take a pause and be mindful of the impact that we could be having on our colleagues. And I know I am with a group of people who are deeply committed to anti-oppression in their work, so, none of this is going to be all that new, but always important to share as we start out.</p>
          </accordion>
        </div>
        <p><a :href="`${publicPath}docs/community/Rigorous-learning.pdf`" target="_blank">Open transcript in a new tab</a></p>
      </div>

    </div>
  </section>
</template>

<script>
import Accordion from '@/components/Accordion.vue'
// import SidebarMenu from '@/components/SidebarMenu.vue'
import {
  Hooper,
  Slide,
  Navigation as HooperNavigation,
  Pagination as HooperPagination
} from 'hooper'
import 'hooper/dist/hooper.css'

export default {
  name: 'content',
  components: {
    // SidebarMenu,
    Accordion,
    Hooper,
    Slide,
    HooperNavigation,
    HooperPagination
  },
  data () {
    return {
      publicPath: process.env.BASE_URL
    }
  },

  mounted () {
  }
}
</script>

<style>
</style>
